import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { ModalControlModalProps, NakedButton, Tray } from '@frontend/design-system';
import type { AvailabilityHeaderFieldProps } from './availability-header.types';
import { ChecklistPopover } from './checklist-popover';

type FiltersTrayProps = Pick<
  AvailabilityHeaderFieldProps,
  | 'isMultiLocation'
  | 'locationOptions'
  | 'locationChecklistField'
  | 'timeRangeOptions'
  | 'timeRangeChecklistField'
  | 'operatories'
  | 'operatoryChecklistField'
  | 'providers'
  | 'providerChecklistField'
> & {
  modalProps: ModalControlModalProps;
};

export const FiltersTray = ({
  isMultiLocation,
  locationOptions,
  locationChecklistField,
  timeRangeOptions,
  timeRangeChecklistField,
  operatories,
  operatoryChecklistField,
  providers,
  providerChecklistField,
  modalProps,
}: FiltersTrayProps) => {
  const { t } = useTranslation('schedule');

  return (
    <Tray {...modalProps} width='small'>
      <Tray.Header
        Buttons={
          <NakedButton onClick={modalProps.onClose} css={styles.closeButton} aria-label={t('Close filters')}>
            <Icon name='x' size={20} color='default' />
          </NakedButton>
        }
      >
        {t('Filters')}
      </Tray.Header>
      <Tray.Body css={styles.body}>
        {isMultiLocation && locationOptions.length > 1 && (
          <ChecklistPopover
            label={t('Location')}
            fieldName='locationIds'
            leadingIcon='location'
            options={locationOptions}
            checklistField={locationChecklistField}
            fullWidth
          />
        )}

        <ChecklistPopover
          label={t('Time of Day')}
          fieldName='timeRanges'
          leadingIcon='clock'
          options={timeRangeOptions}
          checklistField={timeRangeChecklistField}
          fullWidth
        />
        {operatories.length > 0 && (
          <ChecklistPopover
            label={t('Operatory')}
            fieldName='operatoryIds'
            leadingIcon='department'
            options={operatories}
            checklistField={operatoryChecklistField}
            fullWidth
          />
        )}
        {providers.length > 0 && (
          <ChecklistPopover
            label={t('Provider')}
            fieldName='providerIds'
            leadingIcon='user'
            options={providers}
            checklistField={providerChecklistField}
            fullWidth
          />
        )}
      </Tray.Body>
      <Tray.Actions
        primaryLabel={t('Apply')}
        onPrimaryClick={modalProps.onClose}
        secondaryLabel={t('Cancel')}
        onSecondaryClick={modalProps.onClose}
      />
    </Tray>
  );
};

const styles = {
  closeButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2, 0)};
  `,
  filterRow: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `,
  sectionLabel: css`
    padding: ${theme.spacing(0, 0.5)};
  `,
};
